/* global Word, console */
import { useEffect, useCallback } from "react";
import Fuse from "fuse.js";

type FuseItem = { text: string; index: number };
type FuseMatch = {
  indices: ReadonlyArray<readonly [number, number]>;
  key?: string;
  refIndex?: number;
  value?: string;
};

let fuseInstance: Fuse<FuseItem> | null = null;
let isDirty = true;
let indexingPromise: Promise<void> | null = null;

function findLongestMatchIndices(indices: readonly (readonly [number, number])[]): {
  start: number;
  end: number;
  length: number;
} {
  let longestMatch = { start: 0, end: 0, length: 0 };
  indices.forEach(([start, end]) => {
    const length = end - start + 1;
    if (length > longestMatch.length) {
      longestMatch = { start, end, length };
    }
  });
  return longestMatch;
}

async function selectBySubstringMatch(
  context: Word.RequestContext,
  matches: ReadonlyArray<FuseMatch> | undefined,
  itemText: string
): Promise<boolean> {
  if (matches == null || matches.length === 0) return false;

  const firstMatch = matches[0];
  if (firstMatch == null) return false;

  const longestMatch = findLongestMatchIndices(firstMatch.indices);
  if (longestMatch.length <= 3) return false;

  const matchedText = itemText.substring(longestMatch.start, longestMatch.end + 1);
  const searchResults = context.document.body.search(matchedText, {
    ignorePunct: true,
    ignoreSpace: true,
  });
  context.load(searchResults, "items");
  await context.sync();

  if (searchResults.items.length > 0) {
    searchResults.items[0]?.select();
    await context.sync();
    return true;
  }
  return false;
}

async function selectByFullTextFallback(
  context: Word.RequestContext,
  fullText: string
): Promise<boolean> {
  const fallbackResults = context.document.body.search(fullText, {
    ignorePunct: false,
    ignoreSpace: false,
  });
  context.load(fallbackResults, "items");
  await context.sync();

  if (fallbackResults.items.length > 0) {
    fallbackResults.items[0]?.select();
    await context.sync();
    return true;
  }
  return false;
}

function buildIndex(): Promise<void> {
  if (indexingPromise) return indexingPromise;

  indexingPromise = (async () => {
    try {
      await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        context.load(paragraphs, "text");
        await context.sync();

        const paragraphTexts = paragraphs.items.map((p, index) => ({
          text: p.text,
          index: index,
        }));

        fuseInstance = new Fuse(paragraphTexts, {
          keys: ["text"],
          includeScore: true,
          includeMatches: true,
          threshold: 0.4,
          ignoreLocation: true,
        });

        isDirty = false;
      });
    } catch (error) {
      console.error("Error building fuzzy search index:", error);
    } finally {
      indexingPromise = null;
    }
  })();

  return indexingPromise;
}

async function fuzzySearchInDocument(text: string): Promise<boolean> {
  try {
    if (isDirty || !fuseInstance) {
      await buildIndex();
    }

    const results = fuseInstance?.search(text) ?? [];
    if (results.length === 0) return false;

    const bestMatch = results[0];
    if (bestMatch == null) return false;

    return await Word.run(async (context) => {
      const found = await selectBySubstringMatch(context, bestMatch.matches, bestMatch.item.text);
      if (found) return true;
      return selectByFullTextFallback(context, bestMatch.item.text);
    });
  } catch (error) {
    console.error("Error in fuzzySearchInDocument:", error);
    return false;
  }
}

function removeEventHandler(
  eventHandler: OfficeExtension.EventHandlerResult<Word.ParagraphChangedEventArgs>,
  trackedDocument: Word.Document
): void {
  Word.run(trackedDocument, async (context) => {
    eventHandler.remove();
    await context.sync();
  }).catch(console.error);
}

export const useFuzzySearch = () => {
  const stableBuildIndex = useCallback(() => buildIndex(), []);

  useEffect(() => {
    isDirty = true;

    let eventHandler: OfficeExtension.EventHandlerResult<Word.ParagraphChangedEventArgs> | null =
      null;
    let trackedDocument: Word.Document | null = null;
    let disposed = false;

    const setupListener = async () => {
      if (isDirty && !fuseInstance) {
        await stableBuildIndex();
      }
      try {
        await Word.run(async (context) => {
          trackedDocument = context.document;
          eventHandler = context.document.onParagraphChanged.add(() => {
            isDirty = true;
            return Promise.resolve();
          });
          await context.sync();
        });
      } catch (error) {
        console.error("Failed to setup document change listener:", error);
        return;
      }
      if (disposed && eventHandler && trackedDocument) {
        removeEventHandler(eventHandler, trackedDocument);
      }
    };

    setupListener();

    return () => {
      disposed = true;
      isDirty = true;
      if (eventHandler && trackedDocument) {
        removeEventHandler(eventHandler, trackedDocument);
      }
    };
  }, [stableBuildIndex]);

  return { fuzzySearchInDocument, buildIndex };
};
